import { useEffect, useMemo, useRef, useState } from "react";
import type { AggregatedQto } from "../../../../integrations/qto/types";
import type { QtoSourceId } from "../../../../integrations/qto/registry";
import { useProject } from "../../../context/ProjectContext";
import { useDialog } from "../../../context/DialogContext";
import { useBanner } from "../../../context/BannerContext";
import { DialogDescription, DialogSuccessTitle } from "../../../shared/dialog/Dialog";
import { deleteQtoReviewWorksheet } from "../../../../excel/takeoff/reviewLog";
import { openEstimateInEdiphi } from "../../../../utils/links";
import {
  getQtoReviewSheetGeneration,
  useQtoReviewActions,
  useQtoReviewRowCounts,
  useQtoReviewSheetGeneration,
} from "../../../hooks/useQtoActions";
import {
  CANCEL_ACTION,
  OK_ACTION,
  QTO_IMPORT_SUCCESS_TITLE,
  QTO_REVIEW_CREATED,
  QTO_REVIEW_CREATE_ERROR,
  QTO_REVIEW_CREATING_TOOLTIP,
  QTO_REVIEW_LINE_ITEMS_ERROR_TOOLTIP,
  QTO_REVIEW_LOADING_TOOLTIP,
  QTO_REVIEW_NEEDS_ESTIMATE,
  QTO_REVIEW_NOTHING_TOOLTIP,
  QTO_REVIEW_SUBMITTING_TOOLTIP,
  qtoImportSuccess,
} from "../../../constants";

export interface QtoSummaryOptions {
  qtoSourceId: QtoSourceId;
  aggregatedQtos: AggregatedQto[] | null;
  takeoffItemCount: number | null;
  lastUpdated: string | null;
  emptyTooltip: string;
}

const countText = (value: number | null, loading: boolean, error: boolean): string => {
  if (error) return "—";
  if (loading || value == null) return "...";
  return String(value);
};

/**
 * Review / Submit state for one QTO source: the counts, the rules gating each button, and the
 * two handlers.
 *
 * Lives beside `TakeOffDatasourceCard`, its only caller, rather than in `ui/hooks/`. What it returns
 * is mostly derived render state — tooltips, disabled flags, formatted counts — and its two handlers
 * wrap `useQtoActions` with banners and a dialog. That is a component's job, not an actions hook's:
 * the card is already the shared thing across Togal, ATO and OST, so nothing drifts by living here.
 */
export function useQtoSummary({
  qtoSourceId,
  aggregatedQtos,
  takeoffItemCount,
  lastUpdated,
  emptyTooltip,
}: QtoSummaryOptions) {
  const { selectedEstimate } = useProject();
  const { showBanner } = useBanner();
  const { showDialog, hideDialog } = useDialog();
  const { createQtoReviewSheet, submitQtoReviewToEstimate } = useQtoReviewActions();
  const { counts, loadingLineItems, lineItemsError, refreshLineItems } = useQtoReviewRowCounts(
    selectedEstimate?.id,
    aggregatedQtos
  );

  const [isExporting, setIsExporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const lastOwnReviewGenerationRef = useRef<number | null>(null);
  const sheetGeneration = useQtoReviewSheetGeneration();

  const clearReviewSubmitReady = () => {
    lastOwnReviewGenerationRef.current = null;
    setHasReviewed(false);
  };

  // Drop submit-ready state when another source rebuilds the QTO Review worksheet.
  useEffect(() => {
    if (!hasReviewed) return;
    if (lastOwnReviewGenerationRef.current === null) return;
    if (sheetGeneration !== lastOwnReviewGenerationRef.current) {
      lastOwnReviewGenerationRef.current = null;
      setHasReviewed(false);
    }
  }, [sheetGeneration, hasReviewed]);

  // Submit-ready state belongs to the estimate the review sheet was built against: its Add rows were
  // computed as missing from that estimate and carry no line item id, so submitting them anywhere else
  // creates them in the wrong place. Covers removal too, which is just the id going undefined.
  useEffect(() => {
    clearReviewSubmitReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstimate?.id]);

  useEffect(() => {
    if (!selectedEstimate?.id) return;
    refreshLineItems();
  }, [selectedEstimate?.id, lastUpdated, refreshLineItems]);

  const hasReviewWork = counts != null && counts.toAdd + counts.toUpdate > 0;
  const reviewDisabled =
    isExporting ||
    // A submit reads the QTO Review worksheet, so rebuilding it mid-submit would move the ground
    // under it.
    isSubmitting ||
    !aggregatedQtos ||
    aggregatedQtos.length === 0 ||
    loadingLineItems ||
    lineItemsError ||
    counts === null ||
    !hasReviewWork;

  const reviewTooltip = useMemo(() => {
    if (isExporting) return QTO_REVIEW_CREATING_TOOLTIP;
    if (isSubmitting) return QTO_REVIEW_SUBMITTING_TOOLTIP;
    if (!aggregatedQtos || aggregatedQtos.length === 0) return emptyTooltip;
    if (lineItemsError) {
      return QTO_REVIEW_LINE_ITEMS_ERROR_TOOLTIP;
    }
    if (loadingLineItems) {
      return QTO_REVIEW_LOADING_TOOLTIP;
    }
    if (counts !== null && !hasReviewWork) return QTO_REVIEW_NOTHING_TOOLTIP;
    return "";
  }, [
    isExporting,
    isSubmitting,
    aggregatedQtos,
    lineItemsError,
    loadingLineItems,
    counts,
    hasReviewWork,
    emptyTooltip,
  ]);

  const showCountSkeleton = aggregatedQtos == null || loadingLineItems;

  const handleReview = async () => {
    if (!aggregatedQtos) return;
    setIsExporting(true);
    try {
      await createQtoReviewSheet(aggregatedQtos, selectedEstimate?.id ?? null);
      showBanner(QTO_REVIEW_CREATED, "success");
      lastOwnReviewGenerationRef.current = getQtoReviewSheetGeneration();
      setHasReviewed(true);
    } catch {
      showBanner(QTO_REVIEW_CREATE_ERROR, "error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedEstimate) {
      showBanner(QTO_REVIEW_NEEDS_ESTIMATE, "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const { rowCount, reviewSheetEmpty, canDeleteReviewSheet } = await submitQtoReviewToEstimate(
        selectedEstimate.id,
        { qtoSourceId }
      );

      if (reviewSheetEmpty) {
        if (canDeleteReviewSheet) {
          const del = await deleteQtoReviewWorksheet();
          if (!del.success) {
            showBanner(del.error?.message ?? "Could not delete the review sheet", "error");
          }
        }
        clearReviewSubmitReady();
      }

      const dismissWithoutNavigation = () => {
        hideDialog();
      };

      showDialog({
        title: DialogSuccessTitle(QTO_IMPORT_SUCCESS_TITLE),
        description: DialogDescription(qtoImportSuccess(rowCount, selectedEstimate.name)),
        cancel: {
          label: CANCEL_ACTION,
          action: dismissWithoutNavigation,
        },
        onClose: dismissWithoutNavigation,
        submit: {
          label: OK_ACTION,
          action: () => {
            hideDialog();
            openEstimateInEdiphi(selectedEstimate.project, selectedEstimate.id, { calc: true });
          },
        },
      });
      refreshLineItems();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to import line items";
      showBanner(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    total: takeoffItemCount,
    toAdd: counts?.toAdd ?? null,
    toUpdate: counts?.toUpdate ?? null,
    totalText: countText(takeoffItemCount, takeoffItemCount == null, false),
    newText: countText(counts?.toAdd ?? null, showCountSkeleton, lineItemsError),
    updatedText: countText(counts?.toUpdate ?? null, showCountSkeleton, lineItemsError),
    showCountSkeleton,
    lineItemsError,
    reviewDisabled,
    reviewTooltip,
    submitDisabled: !hasReviewed || isSubmitting,
    isExporting,
    isSubmitting,
    handleReview,
    handleSubmit,
  };
}

export type QtoSummary = ReturnType<typeof useQtoSummary>;

export default useQtoSummary;
